import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import applicationService, { Application } from "../services/api/applicationService";

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);

  // PayFast callback parameters (handle both uppercase and lowercase)
  const basketId = searchParams.get("BASKET_ID") || searchParams.get("basket_id");
  const transactionId = searchParams.get("TRANSACTION_ID") || searchParams.get("transaction_id");
  const responseCode = searchParams.get("RESPONSE_CODE") || searchParams.get("err_code");

  // Get application ID from query parameter (added by backend in callback URL)
  const applicationId = searchParams.get("applicationId");

  console.log('PaymentSuccess - basketId:', basketId);
  console.log('PaymentSuccess - transactionId:', transactionId);
  console.log('PaymentSuccess - applicationId:', applicationId);

  useEffect(() => {
    if (applicationId) {
      // If we have PayFast callback data, send it to backend first
      if (basketId && transactionId) {
        handlePayFastCallback();
      } else {
        checkPaymentStatus();
      }
    } else {
      setError("No application ID provided");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, basketId, transactionId]);

  const handlePayFastCallback = async () => {
    try {
      // Send callback data to backend (use actual parameter names from PayFast)
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/gopayfast/callback/success`, {
        BASKET_ID: basketId,
        TRANSACTION_ID: transactionId,
        TRANSACTION_STATUS: searchParams.get("TRANSACTION_STATUS") || searchParams.get("transaction_status"),
        RESPONSE_CODE: responseCode,
        RESPONSE_MESSAGE: searchParams.get("RESPONSE_MESSAGE") || searchParams.get("err_msg"),
        TXNAMT: searchParams.get("TXNAMT") || searchParams.get("transaction_amount"),
        ORDER_DATE: searchParams.get("ORDER_DATE") || searchParams.get("order_date"),
      });

      // Then check payment status
      await checkPaymentStatus();
    } catch (err: any) {
      console.error("Callback error:", err);
      // Still try to check payment status
      await checkPaymentStatus();
    }
  };

  const checkPaymentStatus = async () => {
    try {
      console.log('Checking payment status for application:', applicationId);

      // First check payment status via dedicated API
      try {
        const paymentStatus = await applicationService.getPaymentStatus(applicationId!);
        console.log('Payment status API response:', paymentStatus);
      } catch (statusErr: any) {
        console.error('Payment status API error:', statusErr);
        // Continue to get application details even if status check fails
      }

      // Then get full application details
      console.log('Fetching application details...');
      const application = await applicationService.getApplicationById(applicationId!);
      console.log('Application details:', application);
      setApplication(application);

      // Verify payment is completed
      if (application.paymentStatus !== "COMPLETED") {
        console.warn('Payment not completed:', application.paymentStatus);
        setError("Payment verification in progress. Please wait a moment and refresh the page.");
      } else {
        console.log('✓ Payment verified successfully');
      }
    } catch (err: any) {
      console.error('Check payment status error:', err);
      setError(err.message || "Failed to verify payment status");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!applicationId) {
      setError("Missing application information");
      return;
    }

    // Payment webhook already transitions APPROVED → SUBMITTED
    // Just redirect to my-applications
    navigate("/my-applications");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100">
              <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Payment Verification</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6 space-y-3">
              <button
                onClick={checkPaymentStatus}
                className="w-full px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Check Again
              </button>
              <button
                onClick={() => navigate("/my-applications")}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Go to My Applications
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">Payment Successful!</h2>
          <p className="mt-2 text-gray-600">Your payment has been processed successfully.</p>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Application Number:</span>
              <span className="font-medium text-gray-900">{application?.applicationNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Course:</span>
              <span className="font-medium text-gray-900">{application?.course?.title}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">University:</span>
              <span className="font-medium text-gray-900">{application?.course?.university?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Payment Status:</span>
              <span className={`font-medium ${application?.paymentStatus === "COMPLETED"
                  ? "text-green-600"
                  : "text-yellow-600"
                }`}>
                {application?.paymentStatus === "COMPLETED" ? "✓ Completed" : "⏳ Processing"}
              </span>
            </div>
            {application?.paymentReference && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-mono text-xs text-gray-900">{application.paymentReference}</span>
              </div>
            )}
          </div>
        </div>

        {application?.paymentStatus === "COMPLETED" ? (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>✓ Payment Verified!</strong> Click the button below to submit your application for review.
              Once submitted, our team will review your application and documents.
            </p>
          </div>
        ) : (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⏳ Payment Processing:</strong> Your payment is being verified.
              This usually takes a few moments. Please refresh the page or check back shortly.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleSubmitApplication}
            disabled={loading || application?.paymentStatus !== "COMPLETED"}
            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Submitting..." : "Submit Application"}
          </button>
          <button
            onClick={() => navigate("/my-applications")}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            View My Applications
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
